/**
 * DealComparables Component - Historical deal comparison for pricing insights
 * Phase 5 Part 3: Deal Comparables Database UI
 */

import { useState, useEffect } from 'react';
import { comparablesService } from '../services/comparablesService';
import {
  DollarSign,
  TrendingUp,
  BarChart3,
  Building2,
  Calendar,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Info,
  Loader2,
  Search
} from 'lucide-react';

// Format money values
function formatMoney(amount) {
  if (amount === null || amount === undefined || amount === '') return 'N/A';
  // Convert string to number if needed (API may return strings)
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(num)) return 'N/A';
  if (num >= 1000) {
    return `$${(num / 1000).toFixed(1)}B`;
  }
  return `$${num.toFixed(0)}M`;
}

// Format date
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.getFullYear().toString();
}

// Stat box component
function StatBox({ label, value, subLabel, icon: Icon, color = 'purple' }) {
  const colorClasses = {
    purple: 'bg-purple-50 text-purple-600',
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    gray: 'bg-gray-50 text-gray-600'
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subLabel && (
            <p className="text-xs text-gray-400 mt-1">{subLabel}</p>
          )}
        </div>
        {Icon && (
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        )}
      </div>
    </div>
  );
}

// Similarity badge
function SimilarityBadge({ score }) {
  let bgColor, textColor;
  if (score >= 85) {
    bgColor = 'bg-green-100';
    textColor = 'text-green-700';
  } else if (score >= 70) {
    bgColor = 'bg-blue-100';
    textColor = 'text-blue-700';
  } else if (score >= 50) {
    bgColor = 'bg-yellow-100';
    textColor = 'text-yellow-700';
  } else {
    bgColor = 'bg-gray-100';
    textColor = 'text-gray-700';
  }

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${bgColor} ${textColor}`}>
      {score}% match
    </span>
  );
}

// Deal row component
function DealRow({ deal, expanded, onToggle }) {
  return (
    <div className="border-b border-gray-100 last:border-0">
      <div
        className="flex items-center gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={onToggle}
      >
        {/* Year */}
        <div className="w-16 flex-shrink-0">
          <span className="text-sm font-medium text-gray-900">
            {formatDate(deal.deal_date)}
          </span>
        </div>

        {/* Companies */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">
              {deal.mnc_company}
            </span>
            <span className="text-gray-400">acquired from</span>
            <span className="text-gray-700 truncate">{deal.licensor}</span>
          </div>
          {deal.project_name && (
            <p className="text-sm text-gray-500 truncate mt-0.5">
              {deal.project_name}
            </p>
          )}
        </div>

        {/* Deal Values */}
        <div className="text-right flex-shrink-0">
          <div className="font-semibold text-gray-900">
            {formatMoney(deal.upfront_payment_usd_millions)}
          </div>
          {deal.total_value_usd_millions && (
            <div className="text-xs text-gray-500">
              {formatMoney(deal.total_value_usd_millions)} total
            </div>
          )}
        </div>

        {/* Similarity */}
        <div className="flex-shrink-0">
          <SimilarityBadge score={deal.similarity_score} />
        </div>

        {/* Expand icon */}
        <div className="flex-shrink-0 text-gray-400">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div className="px-4 pb-4 bg-gray-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Therapeutic Area</p>
              <p className="font-medium">{deal.therapeutic_area || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Stage</p>
              <p className="font-medium">{deal.stage || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Modality</p>
              <p className="font-medium">{deal.modality || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Deal Type</p>
              <p className="font-medium capitalize">{deal.deal_type || '-'}</p>
            </div>
          </div>
          {deal.deal_summary && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <p className="text-gray-500 text-sm">Deal Summary</p>
              <p className="text-sm mt-1 text-gray-700">{deal.deal_summary}</p>
            </div>
          )}
          {deal.milestone_payment_usd_millions && (
            <div className="mt-3 flex items-center gap-4 text-sm">
              <span className="text-gray-500">Milestones:</span>
              <span className="font-medium">{formatMoney(deal.milestone_payment_usd_millions)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Main component
export function DealComparables({ asset, className = '' }) {
  const [comparables, setComparables] = useState([]);
  const [stats, setStats] = useState(null);
  const [topAcquirers, setTopAcquirers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedDeal, setExpandedDeal] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const [activeTherapeuticArea, setActiveTherapeuticArea] = useState(null);

  // Get therapeutic area from asset
  const therapeuticArea = asset?.therapeutic_areas?.[0] ||
    asset?.therapeutic_area ||
    asset?.primary_therapeutic_area ||
    'Oncology'; // Default

  // Fallback therapeutic area (broader category)
  const fallbackTherapeuticArea = asset?.fallback_therapeutic_area || 'Oncology';

  const stage = asset?.development_stage || asset?.stage;
  const modality = asset?.modality || asset?.tech_platforms?.[0];

  useEffect(() => {
    async function loadData() {
      if (!therapeuticArea) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      setUsedFallback(false);

      try {
        // Try to use RPC functions first, fallback to direct query
        let compsData, statsData, acquirersData;
        let searchArea = therapeuticArea;

        try {
          [compsData, statsData, acquirersData] = await Promise.all([
            comparablesService.findComparables({
              therapeutic_area: therapeuticArea,
              stage,
              modality
            }),
            comparablesService.getDealStatistics(therapeuticArea, stage),
            comparablesService.getTopAcquirers(therapeuticArea, 5)
          ]);

          // If no results and we have a different fallback, try the fallback
          if ((!compsData || compsData.length === 0) &&
              fallbackTherapeuticArea &&
              fallbackTherapeuticArea.toLowerCase() !== therapeuticArea.toLowerCase()) {
            console.log(`No results for "${therapeuticArea}", falling back to "${fallbackTherapeuticArea}"`);

            [compsData, statsData, acquirersData] = await Promise.all([
              comparablesService.findComparables({
                therapeutic_area: fallbackTherapeuticArea,
                stage,
                modality
              }),
              comparablesService.getDealStatistics(fallbackTherapeuticArea, stage),
              comparablesService.getTopAcquirers(fallbackTherapeuticArea, 5)
            ]);

            searchArea = fallbackTherapeuticArea;
            setUsedFallback(true);
          }
        } catch (rpcError) {
          // Fallback to direct query if RPC not available
          console.warn('RPC functions not available, using direct query:', rpcError);
          compsData = await comparablesService.queryDeals({
            therapeutic_area: therapeuticArea,
            stage,
            limit: 15
          });

          // If no results, try fallback
          if ((!compsData || compsData.length === 0) &&
              fallbackTherapeuticArea &&
              fallbackTherapeuticArea.toLowerCase() !== therapeuticArea.toLowerCase()) {
            compsData = await comparablesService.queryDeals({
              therapeutic_area: fallbackTherapeuticArea,
              stage,
              limit: 15
            });
            searchArea = fallbackTherapeuticArea;
            setUsedFallback(true);
          }

          // Add similarity scores manually
          compsData = compsData.map(d => ({
            ...d,
            similarity_score: d.therapeutic_area?.includes(searchArea) ?
              (d.stage?.includes(stage) ? 85 : 70) : 50
          }));

          statsData = comparablesService.calculateLocalStatistics(compsData);
          acquirersData = [];
        }

        setComparables(compsData || []);
        setStats(statsData);
        setTopAcquirers(acquirersData || []);
        setActiveTherapeuticArea(searchArea);
      } catch (err) {
        console.error('Error loading comparables:', err);
        setError(err.message || 'Failed to load comparable deals');
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [therapeuticArea, fallbackTherapeuticArea, stage, modality]);

  if (loading) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-8 ${className}`}>
        <div className="flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          <span className="ml-3 text-gray-500">Loading comparable deals...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-xl border border-gray-200 p-6 ${className}`}>
        <div className="text-center text-red-500">
          <p className="font-medium">Error loading comparables</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      </div>
    );
  }

  // Separate deals into modality-matched and other deals
  const modalityMatchedDeals = modality
    ? comparables.filter(d => d.modality && d.modality.toLowerCase().includes(modality.toLowerCase()))
    : [];
  const otherDeals = modality
    ? comparables.filter(d => !d.modality || !d.modality.toLowerCase().includes(modality.toLowerCase()))
    : comparables;

  const displayedModalityDeals = showAll ? modalityMatchedDeals : modalityMatchedDeals.slice(0, 5);
  const displayedOtherDeals = showAll ? otherDeals : otherDeals.slice(0, 5);

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <DollarSign className="w-5 h-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {asset?.asset_name ? `Comparables for ${asset.asset_name}` : 'Deal Comparables'}
            </h2>
            <p className="text-sm text-gray-500">
              {modality ? `${modality} deals in ${activeTherapeuticArea || therapeuticArea}` : `Deals in ${activeTherapeuticArea || therapeuticArea}`}
              {stage && ` • ${stage}`}
            </p>
            {usedFallback && (
              <p className="text-xs text-amber-600 mt-1">
                No exact matches for "{therapeuticArea}" — showing broader {activeTherapeuticArea} deals
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Info className="w-4 h-4" />
          <span>{stats?.deal_count || comparables.length} deals</span>
        </div>
      </div>

      {/* Statistics Grid */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatBox
            label="Average Upfront"
            value={formatMoney(stats.avg_upfront_usd_millions || stats.avg_upfront)}
            subLabel={stats.median_upfront_usd_millions ? `Median: ${formatMoney(stats.median_upfront_usd_millions)}` : null}
            icon={DollarSign}
            color="green"
          />
          <StatBox
            label="Average Total Value"
            value={formatMoney(stats.avg_total_usd_millions || stats.avg_total)}
            subLabel={stats.median_total_usd_millions ? `Median: ${formatMoney(stats.median_total_usd_millions)}` : null}
            icon={TrendingUp}
            color="blue"
          />
          <StatBox
            label="Deal Range"
            value={stats.min_upfront_usd_millions && stats.max_upfront_usd_millions ?
              `${formatMoney(stats.min_upfront_usd_millions)} - ${formatMoney(stats.max_upfront_usd_millions)}` :
              'N/A'}
            subLabel="Upfront payments"
            icon={BarChart3}
            color="purple"
          />
          <StatBox
            label="Recent Activity"
            value={stats.recent_deal_count || 0}
            subLabel="Deals in last 2 years"
            icon={Calendar}
            color="orange"
          />
        </div>
      )}

      {/* Top Acquirers */}
      {topAcquirers.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-400" />
            Most Active Buyers in {activeTherapeuticArea || therapeuticArea}
          </h3>
          <div className="flex flex-wrap gap-2">
            {topAcquirers.map((acquirer, i) => (
              <div
                key={i}
                className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm"
              >
                <span className="font-medium text-gray-900">{acquirer.mnc_company}</span>
                <span className="text-gray-500 ml-2">{acquirer.deal_count} deals</span>
                {acquirer.total_invested_usd_millions && (
                  <span className="text-gray-400 ml-1">
                    ({formatMoney(acquirer.total_invested_usd_millions)})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modality-Matched Deals */}
      {modality && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-200 bg-purple-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-purple-900">{modality} Deals</h3>
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
                  Direct Match
                </span>
              </div>
              <span className="text-sm text-purple-600">{modalityMatchedDeals.length} deals</span>
            </div>
          </div>

          {modalityMatchedDeals.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-500">No {modality} deals found in database</p>
              <p className="text-sm text-gray-400 mt-1">
                See other {therapeuticArea} deals below for market context
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {displayedModalityDeals.map((deal) => (
                <DealRow
                  key={deal.id}
                  deal={deal}
                  expanded={expandedDeal === deal.id}
                  onToggle={() => setExpandedDeal(expandedDeal === deal.id ? null : deal.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Other Deals in Therapeutic Area */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="font-medium text-gray-900">
                {modality ? `Other ${therapeuticArea} Deals` : 'Comparable Transactions'}
              </h3>
              {modality && (
                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                  Market Context
                </span>
              )}
            </div>
            <span className="text-sm text-gray-500">{otherDeals.length} deals</span>
          </div>
        </div>

        {otherDeals.length === 0 ? (
          <div className="p-8 text-center">
            <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No comparable deals found</p>
            <p className="text-sm text-gray-400 mt-1">
              This could indicate a unique market opportunity
            </p>
          </div>
        ) : (
          <>
            <div className="divide-y divide-gray-100">
              {displayedOtherDeals.map((deal) => (
                <DealRow
                  key={deal.id}
                  deal={deal}
                  expanded={expandedDeal === deal.id}
                  onToggle={() => setExpandedDeal(expandedDeal === deal.id ? null : deal.id)}
                />
              ))}
            </div>

            {otherDeals.length > 5 && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={() => setShowAll(!showAll)}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center gap-1"
                >
                  {showAll ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Show Less
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Show All {otherDeals.length} Deals
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Disclaimer */}
      <p className="text-xs text-gray-400 text-center">
        Deal data sourced from public filings and press releases.
        Values may vary based on milestone achievement.
      </p>
    </div>
  );
}

export default DealComparables;
